/* eslint-disable @typescript-eslint/no-explicit-any */
import { AnyMemberIsObject, AnyObject } from 'types';

const INDEX_OFFSET = 1;

export function sumUp<Item extends number | AnyObject>(
  list: Item[],
  ...config: AnyMemberIsObject<Item> extends never ? [] : [keyof Exclude<Item, number>]
): number {
  let total = 0;

  const [refProp] = config;

  list.forEach((item) => {
    const value = typeof item === 'number' ? item : Number(item[refProp as keyof typeof item]);

    total += (value as number) || 0;
  });

  return total;
}

export function ensureMaxArraySize<Entity = any>(items: Entity[], lengthLimit: number): Entity[][] {
  if (items.length <= lengthLimit) return [items];

  const numberOfSubArrays = Math.ceil(items.length / lengthLimit);

  const subArrays = Array.from({ length: numberOfSubArrays }).map((_, index) => {
    const start = index * lengthLimit;
    const end = (index + INDEX_OFFSET) * lengthLimit;

    return items.slice(start, end);
  });

  return subArrays;
}

function isTruthy<T>(item: T): item is NonNullable<T> {
  return !!item;
}

export function toTruthyList<T>(list: T[]): NonNullable<T>[] {
  return list.filter(isTruthy);
}

export function toUniqueList<T>(list: T[]): T[] {
  return Array.from(new Set(list));
}

export function ensureArray<E>(item: E | E[]): E[] {
  return Array.isArray(item) ? item : [item];
}

export function hasFalsy(list: any[]) {
  return list.some((x) => !x);
}

/**
 * Easily create arrays of N elements
 *
 * Syntax sugar for the `Array.from()` logic
 */
export function arrayFrom<MapFn extends ((k: number) => any) | undefined>(
  length: number,
  mapfn?: MapFn,
) {
  return Array.from({ length }, (_, i) => mapfn?.(i)) as MapFn extends (
    ...any: any[]
  ) => infer Result
    ? Result[]
    : undefined[];
}
